import React, { useEffect, useState } from 'react';
import { find, map, join, isEmpty } from 'lodash';
import DownloadMessaging from '../../../common/downloadMessaging';
import MarkupTool from '../services/markupTool';
import {
  reportRequest,
  saveFilter,
  saveSettingsRequest,
  saveUserSettingsRequest, updateRegionRequest
} from '../requests';
import { FocusBaseContext } from '../contexts';
import { stringifyFilterData } from "../../../../express/researcher/common/helpers";

const IndexDataProvider = ({ summary, project, focus, children }) => {
  const [ DownloadMessagingInstance ] = useState(() => (new DownloadMessaging));
  const [ reportPath ] = useState(() => (focus.report_paths.report_path));
  const [ currentSummary, setCurrentSummary ] = useState(() => (summary));
  const [ heatmap, setHeatmap ] = useState(() => (currentSummary.report.heatmap));
  const [ followups, setFollowups ] = useState(() => (currentSummary.report.followups));
  const [ onReloadMarkupsRequest, setOnReloadMarkupRequest ] = useState(null);
  const [ aiSummaries, setAiSummaries ] = useState(focus.ai_summaries || []);

  const onMarkupSettingsChange = (value) => {
    setOnReloadMarkupRequest(value || {});
  };

  const selectedConceptId = (newSummary, newHeatmap) => {
    const data = newSummary || currentSummary;
    const heatmapData = newHeatmap || heatmap;
    if (heatmapData.changed) {
      return heatmapData.concept.id;
    }
    return data.selected_concept_id ? data.selected_concept_id : heatmapData.concept.id;
  };
  const allSelectedSettings = (newSummary) => ((newSummary || currentSummary).settings);
  const selectedConceptSettings = (newSummary, newHeatmap) => {
    const conceptId = selectedConceptId(newSummary, newHeatmap);
    const settings = allSelectedSettings(newSummary) || {};
    if (conceptId && settings[conceptId]) {
      return settings[conceptId];
    }
    return {};
  };
  const findCurrentKinds = (newSummary, newHeatmap) => (
    selectedConceptSettings(newSummary, newHeatmap).selected_reactions ||
    { positive: true, negative: true, confusing: true }
  );
  const [ currentKinds, setCurrentKinds ] = useState(findCurrentKinds);
  useEffect(() => {
    setCurrentKinds(findCurrentKinds());
  }, [ currentSummary, heatmap ]);
  const updateCurrentKinds = (newHash) => {
    selectedConceptSettings().selected_reactions = newHash;
    setCurrentKinds(newHash);
  };

  const findMarkupType = (newSummary, newHeatmap) => (
    selectedConceptSettings(newSummary, newHeatmap).markup_type
  );
  const [ markupType, setMarkupType ] = useState(findMarkupType);
  useEffect(() => {
    setMarkupType(findMarkupType());
  }, [ currentSummary, heatmap ]);
  const updateMarkupType = (value) => {
    selectedConceptSettings().markup_type = value;
    setMarkupType(value);
  };

  const findRegionId = (newSummary, newHeatmap) => (
    selectedConceptSettings(newSummary, newHeatmap).selected_region_id
  );
  const [ selectedRegionId, setSelectedRegionId ] = useState(findRegionId);
  const [ , setUpdateRegion ] = useState(null);
  useEffect(() => {
    setSelectedRegionId(findRegionId());
  }, [ currentSummary, heatmap ]);
  const updateSelectedRegionId = (value) => {
    selectedConceptSettings().selected_region_id = value;
    setSelectedRegionId(value);
  };
  const refreshRegionData = () => {
    setUpdateRegion({});
  };

  const conceptById = (customId) => {
    const id = customId || selectedConceptId();
    const concept = find(currentSummary.report.concepts, (concept) => (concept.id === id));
    return concept ? concept : (currentSummary.report.concepts[0] || { name: 'Not selected' });
  };

  const [ regions, setRegions ] = useState(conceptById().regions);

  useEffect(() => {
    setRegions(conceptById().regions);
  }, [ currentSummary, heatmap ]);

  const [ markupTool ] = useState(() => {
    const tool = new MarkupTool(
      heatmap.filtered_comments,
      currentKinds,
      conceptById().regions,
      selectedRegionId,
      null,
      markupType
    );
    tool.setAllComments(heatmap.filtered_comments, selectedRegionId);
    return tool;
  });

  const saveUserSettings = (params) => {
    saveUserSettingsRequest(currentSummary.save_user_settings_path, params);
  };
  const saveSettings = (params) => {
    saveSettingsRequest(currentSummary.save_settings_path, params);
  };
  const getConceptSetting = (name) => {
    // name: heatmap_resolution or heatmap_threshold or markup_opacity
    const currentSettings = selectedConceptSettings();
    return currentSettings[name];
  };
  const setConceptSetting = (name, value) => {
    // name: heatmap_resolution or heatmap_threshold or markup_opacity
    const currentSettings = selectedConceptSettings();
    saveUserSettings({ concept_id: selectedConceptId(), [name]: value });
    currentSettings[name] = value;
  };

  const findSelectedRegion = (regions, selectedRegionId) => {
    return find(regions, (el) => el.id === selectedRegionId);
  };

  const onChangeSettings = (kind, data) => {
    saveFilter(
      currentSummary.save_filter_path, kind, map(data, (el) => (el.value)),
      (response) => {
        const newSummary = response.data;
        const newHeatmap = newSummary.report.heatmap;
        setCurrentSummary(newSummary);
        setHeatmap(newHeatmap);
        setFollowups(newSummary.report.followups);
        const currentRegionId = findRegionId(newSummary, newHeatmap);
        markupTool.setAllComments(newHeatmap.filtered_comments, currentRegionId);
        if (currentRegionId && kind === 'filter') {
          const selectedRegion = findSelectedRegion(markupTool.regions, currentRegionId);
          updateRegionRequest(selectedRegion.update_path,
            { filters: filtersRow(data), filter_data: stringifyFilterData([ ...newSummary.filter ]) },
            (response) => {
              setRegions(response.data.regions);
            });
        }
        onMarkupSettingsChange();
      }
    );
  };

  const filtersRow = (filter) => {
    if (isEmpty(filter)) {
      return "none";
    }

    return join(map(filter, (item) => item.label), ",");
  };

  const onChangeDemos = (values) => {
    onChangeSettings('demos', values);
  };

  const onChangeFilters = (values) => {
    onChangeSettings('filter', values);
  };

  const openReport = (event, params, customUrl = null) => {
    event.preventDefault();
    const url = customUrl || event.target.getAttribute('href');
    reportRequest(url, params, () => {
      DownloadMessagingInstance.downloadStarts(event);
    }, () => {}, customUrl);
  };

  const indexValue = {
    project, focus,
    onChangeDemos, onChangeFilters, currentSummary, heatmap,
    openReport, reportPath,

    selectedConceptId, conceptById, selectedConceptSettings,
    currentKinds, updateCurrentKinds, findCurrentKinds,
    markupType, updateMarkupType, findMarkupType,
    selectedRegionId, updateSelectedRegionId, findRegionId, refreshRegionData,
    markupTool, setHeatmap, saveUserSettings, getConceptSetting, setConceptSetting,
    onMarkupSettingsChange, onReloadMarkupsRequest, saveSettings, regions, setRegions, filtersRow,
    findSelectedRegion,

    followups,

    aiSummaries, setAiSummaries
  };
  return (
    <FocusBaseContext.Provider value={ indexValue }>
      { children }
    </FocusBaseContext.Provider>
  );
};

export default IndexDataProvider;
